import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GlassCard from '../ui/GlassCard';
import { Profile, Address } from '../../lib/api';
import { formatDisplay } from '../../lib/phone';

interface FormData {
  firstName: string;
  lastName: string;
  title: string;
  tagline: string;
  phone: string;
  email: string;
  website: string;
  accentColor: string;
  address: Address;
}

interface ContactPreviewCardProps {
  formData: FormData;
  profile: Profile | null;
}

type DeviceType = 'iphone' | 'android';

export default function ContactPreviewCard({ formData, profile }: ContactPreviewCardProps) {
  const { t } = useTranslation();
  const [device, setDevice] = useState<DeviceType>('iphone');

  // Combine title and tagline like the vCard does
  const orgLine = [formData.title, formData.tagline].filter(Boolean).join(' - ');
  const fullName = `${formData.firstName || 'First'} ${formData.lastName || 'Last'}`;
  const initials = `${formData.firstName?.[0] || 'F'}${formData.lastName?.[0] || 'L'}`;

  // Format address like vCard: street, city, postal, country
  const formatAddress = (addr: Address | undefined) => {
    if (!addr) return '';
    const parts = [
      addr.streetAddress,
      addr.city,
      addr.postalCode,
      addr.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const addressLine = formatAddress(formData.address);

  return (
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        {t('dashboard.contactPreview.title')}
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {t('dashboard.contactPreview.description')}
      </p>

      {/* Device Toggle */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-lg bg-gray-100 p-1">
          <button
            onClick={() => setDevice('iphone')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              device === 'iphone'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.contactPreview.iPhone')}
          </button>
          <button
            onClick={() => setDevice('android')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              device === 'android'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('dashboard.contactPreview.android')}
          </button>
        </div>
      </div>

      {/* Phone Mockup */}
      <div className="flex justify-center">
        <div className="w-56">
          {device === 'iphone' ? (
            <IPhoneContactPreview
              fullName={fullName}
              initials={initials}
              orgLine={orgLine}
              phone={formData.phone}
              email={formData.email}
              website={formData.website}
              address={addressLine}
              photoUrl={profile?.photoUrl}
              t={t}
            />
          ) : (
            <AndroidContactPreview
              fullName={fullName}
              initials={initials}
              orgLine={orgLine}
              phone={formData.phone}
              email={formData.email}
              website={formData.website}
              address={addressLine}
              photoUrl={profile?.photoUrl}
              t={t}
            />
          )}
        </div>
      </div>
    </GlassCard>
  );
}

interface ContactPreviewProps {
  fullName: string;
  initials: string;
  orgLine: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  photoUrl?: string;
  t: (key: string) => string;
}

function IPhoneContactPreview({ fullName, initials, orgLine, phone, email, website, address, photoUrl, t }: ContactPreviewProps) {
  return (
    <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
      {/* Screen */}
      <div className="bg-gray-100 rounded-[1.5rem] overflow-hidden">
        {/* Status bar with notch */}
        <div className="h-5 bg-gray-100 flex items-center justify-center">
          <div className="w-16 h-3 bg-gray-900 rounded-full" />
        </div>

        {/* iOS Navigation Bar */}
        <div className="bg-gray-100 px-3 py-1 flex items-center justify-between">
          <div className="flex items-center text-blue-500 text-[10px]">
            <ChevronLeftIcon className="w-3 h-3" />
            <span>{t('dashboard.contactPreview.contact')}</span>
          </div>
          <span className="text-blue-500 text-[10px] font-medium">
            {t('dashboard.contactPreview.edit')}
          </span>
        </div>

        {/* Contact Content - iOS Style */}
        <div className="px-3 pb-3">
          {/* Avatar and Name Section */}
          <div className="flex flex-col items-center py-3">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-base font-semibold overflow-hidden">
              {photoUrl ? (
                <img
                  key={photoUrl}
                  src={photoUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            {/* Name */}
            <h3 className="mt-2 font-semibold text-gray-900 text-xs text-center">
              {fullName}
            </h3>
            {/* Organization */}
            {orgLine && (
              <p className="text-gray-500 text-[9px] text-center mt-0.5">
                {orgLine}
              </p>
            )}
          </div>

          {/* Contact Info Cards - iOS Style */}
          <div className="space-y-1.5">
            {/* Phone */}
            {phone && (
              <div className="bg-white rounded-lg p-1.5">
                <div className="text-[8px] text-gray-500 mb-0.5">
                  {t('dashboard.contactPreview.mobile')}
                </div>
                <div className="text-blue-500 text-[10px]">
                  {formatDisplay(phone)}
                </div>
              </div>
            )}

            {/* Email */}
            {email && (
              <div className="bg-white rounded-lg p-1.5">
                <div className="text-[8px] text-gray-500 mb-0.5">
                  {t('dashboard.contactPreview.work')}
                </div>
                <div className="text-blue-500 text-[10px] truncate">
                  {email}
                </div>
              </div>
            )}

            {/* Website */}
            {website && (
              <div className="bg-white rounded-lg p-1.5">
                <div className="text-[8px] text-gray-500 mb-0.5">
                  {t('dashboard.contactPreview.website')}
                </div>
                <div className="text-blue-500 text-[10px] truncate">
                  {website.replace(/^https?:\/\//, '')}
                </div>
              </div>
            )}

            {/* Address */}
            {address && (
              <div className="bg-white rounded-lg p-1.5">
                <div className="text-[8px] text-gray-500 mb-0.5">
                  {t('dashboard.contactPreview.address')}
                </div>
                <div className="text-blue-500 text-[10px] leading-tight">
                  {address}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AndroidContactPreview({ fullName, initials, orgLine, phone, email, website, address, photoUrl, t }: ContactPreviewProps) {
  return (
    <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
      {/* Screen */}
      <div className="bg-white rounded-[1.5rem] overflow-hidden">
        {/* Status bar */}
        <div className="h-4 bg-gray-800 flex items-center justify-end px-3">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-gray-400" />
            <div className="w-3 h-1.5 bg-gray-400 rounded-sm" />
          </div>
        </div>

        {/* Material Toolbar */}
        <div className="bg-white px-2 py-1.5 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ArrowLeftIcon className="w-4 h-4 text-gray-700" />
            <span className="text-gray-900 text-[10px] font-medium truncate max-w-[90px]">
              {fullName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <StarIcon className="w-3 h-3 text-gray-400" />
            <MoreVertIcon className="w-3 h-3 text-gray-700" />
          </div>
        </div>

        {/* Contact Content - Material Style */}
        <div className="px-3 pb-3">
          {/* Avatar and Name Section */}
          <div className="flex flex-col items-center py-3">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full bg-teal-500 flex items-center justify-center text-white text-base font-medium overflow-hidden">
              {photoUrl ? (
                <img
                  key={photoUrl}
                  src={photoUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                initials
              )}
            </div>
            {/* Name */}
            <h3 className="mt-2 font-medium text-gray-900 text-xs text-center">
              {fullName}
            </h3>
            {/* Organization */}
            {orgLine && (
              <p className="text-gray-500 text-[9px] text-center mt-0.5">
                {orgLine}
              </p>
            )}
          </div>

          {/* Contact Info - Material Style */}
          <div className="space-y-2.5">
            {/* Phone */}
            {phone && (
              <div className="flex items-start gap-2.5">
                <PhoneIcon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-gray-900 text-[10px]">
                    {formatDisplay(phone)}
                  </div>
                  <div className="text-[8px] text-gray-500">
                    {t('dashboard.contactPreview.mobile')}
                  </div>
                </div>
              </div>
            )}

            {/* Email */}
            {email && (
              <div className="flex items-start gap-2.5">
                <EmailIcon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-gray-900 text-[10px] truncate">
                    {email}
                  </div>
                  <div className="text-[8px] text-gray-500">
                    {t('dashboard.contactPreview.work')}
                  </div>
                </div>
              </div>
            )}

            {/* Website */}
            {website && (
              <div className="flex items-start gap-2.5">
                <GlobeIcon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-gray-900 text-[10px] truncate">
                    {website.replace(/^https?:\/\//, '')}
                  </div>
                  <div className="text-[8px] text-gray-500">
                    {t('dashboard.contactPreview.website')}
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {address && (
              <div className="flex items-start gap-2.5">
                <MapPinIcon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-gray-900 text-[10px] leading-tight">
                    {address}
                  </div>
                  <div className="text-[8px] text-gray-500">
                    {t('dashboard.contactPreview.address')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  );
}

function MoreVertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function EmailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
